// Modal shown when a Partner clicks "Do Not Cull". Forces a reason category
// pick from the predefined list; the free-text field is only required when
// the selected category is "Other (explain below)". Re-decision pre-populates
// the prior choice for convenience.

import React, { useEffect, useMemo, useState } from 'react';

export default function CullDecisionModal({
  open,
  client,
  categories,
  otherLabel,
  initialCategory,
  initialText,
  saving,
  onSubmit,
  onCancel,
}) {
  const [category, setCategory] = useState(initialCategory || '');
  const [text, setText]         = useState(initialText || '');
  const [touched, setTouched]   = useState(false);

  // Reset when the modal opens for a different client.
  useEffect(() => {
    if (open) {
      setCategory(initialCategory || '');
      setText(initialText || '');
      setTouched(false);
    }
  }, [open, initialCategory, initialText]);

  // Reasons alphabetized, with "Other" always pinned to the bottom.
  const sortedCategories = useMemo(() => {
    const list  = categories || [];
    const other = list.filter(c => c === otherLabel);
    const rest  = list.filter(c => c !== otherLabel).sort((a, b) => a.localeCompare(b));
    return [...rest, ...other];
  }, [categories, otherLabel]);

  if (!open) return null;

  const isOther    = category === otherLabel;
  const textNeeded = isOther && !text.trim();
  const canSubmit  = !!category && !textNeeded && !saving;

  function submit(e) {
    e?.preventDefault?.();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({ reasonCategory: category, reasonText: text.trim() || null });
  }

  return (
    <div className="cull-modal-backdrop" onMouseDown={onCancel}>
      <form
        className="cull-modal"
        onMouseDown={e => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="cull-modal-header">
          <h3>Reason for "Do Not Cull"</h3>
          <div className="cull-modal-client">
            {client?.code} — {client?.name}
          </div>
        </div>

        <div className="cull-modal-body">
          <label className="cull-modal-label" htmlFor="cull-reason-category">
            Reason category <span className="cull-modal-req">*</span>
          </label>
          <select
            id="cull-reason-category"
            className="cull-modal-select"
            value={category}
            onChange={e => setCategory(e.target.value)}
            disabled={saving}
            autoFocus
          >
            <option value="">— select a reason —</option>
            {sortedCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {touched && !category && (
            <div className="cull-modal-err">Pick a reason category.</div>
          )}

          {!!category && (
            <>
              <label className="cull-modal-label" htmlFor="cull-reason-text">
                Explain {isOther
                  ? <span className="cull-modal-req">*</span>
                  : <span className="cull-modal-optional">(optional)</span>}
              </label>
              <textarea
                id="cull-reason-text"
                className="cull-modal-textarea"
                rows={3}
                value={text}
                onChange={e => setText(e.target.value)}
                disabled={saving}
                placeholder={isOther
                  ? 'Provide context for why this client should not be culled…'
                  : 'Add any context (optional)…'}
              />
              {touched && textNeeded && (
                <div className="cull-modal-err">Explanation is required when "Other" is selected.</div>
              )}
            </>
          )}
        </div>

        <div className="cull-modal-footer">
          <button
            type="button"
            className="cull-modal-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cull-modal-submit"
            disabled={!canSubmit}
          >
            {saving ? 'Saving…' : 'Confirm Do Not Cull'}
          </button>
        </div>
      </form>
    </div>
  );
}
